import React from "react";
import IQveraEndpoint from "src/ServerEntities/IQveraEndpoint";
import ErrorBox from 'src/SharedComponents/ErrorBox';
import LoadingIndicator from "src/SharedComponents/LoadingIndicator";
import { SessionContext } from "src/Views/SessionContext";
import { getQveraEndpoints, getRhapsodyHeader } from "../ramService";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { RolesContext } from "src/Roles/RolesContext";
import IRhapsodyServer from "src/ServerEntities/IRhapsodyServer";
import ServerSelector from "src/SharedComponents/Select/ServerSelector";
import useCookie from "src/CustomHooks/useCookie";

const ContentContainer = styled.main`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  border: 4px solid #44A2D6;
  height: 100%;
  width: 80%;
  box-sizing: border-box;
`;

const TableHeader = styled.thead`
`;


const TableRow = styled.tr`
  &:hover {
    background-color: #f2f2f2;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  table-layout: fixed;
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #1577AE;
  width: 16.66%;
  & svg {
    margin-left: 8px;
  }
  &.sort-asc svg {
    transform: rotate(180deg);
  }
`;

const TableCell = styled.td`
  padding: 6px 16px;
  color: #212529;
  font-weight: 600;
  word-wrap: break-word;
  overflow: hidden;
`;


const TitleBar = styled.div`
    margin: 8px;
    border-radius: 20px;
    background-color: #1577AE;
    padding: 5px;
    color: #FFF;
    text-align: center;
    font-size: 24px;
    font-weight: bold;
    box-sizing: border-box;
`;


type SORT_COLUMNS = "serverId" | "endpoint" | "channelName" | "zoneName" | "nodeName" | "endpointType" | "endpointStatus";

const sortEndpoints =
    (sortColumn: SORT_COLUMNS, sortAsc: boolean) =>
    (a: IQveraEndpoint, b: IQveraEndpoint) => {
        if (sortColumn === "serverId") {
            return sortAsc
                ? a.serverId - b.serverId
                : b.serverId - a.serverId;
        }

        if (sortColumn === "endpoint") {
            return sortAsc
                ? a.endpoint.localeCompare(b.endpoint)
                : b.endpoint.localeCompare(a.endpoint);
        }

        if (sortColumn === "channelName") {
            return sortAsc
                ? a.channelName.localeCompare(b.channelName)
                : b.channelName.localeCompare(a.channelName);
        }

        if (sortColumn === "zoneName") {
            return sortAsc
                ? a.zoneName.localeCompare(b.zoneName)
                : b.zoneName.localeCompare(a.zoneName);
        }

        if (sortColumn === "nodeName") {
            return sortAsc
                ? a.nodeName.localeCompare(b.nodeName)
                : b.nodeName.localeCompare(a.nodeName);
        }

        if (sortColumn === "endpointType") {
            return sortAsc
                ? a.endpointType.localeCompare(b.endpointType)
                : b.endpointType.localeCompare(a.endpointType);
        }

        if (sortColumn === "endpointStatus") {
            return sortAsc
                ? a.endpointStatus.localeCompare(b.endpointStatus)
                : b.endpointStatus.localeCompare(a.endpointStatus);
        }

        return 0;
    };


const QveraEndpoints = () => {
    const { state, dispatch } = React.useContext(SessionContext);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [endpoints, setEndpoints] = React.useState<IQveraEndpoint[]>([]);
    const [sortColumn, setSortColumn] = React.useState<SORT_COLUMNS>("zoneName");
    const [sortAsc, setSortAsc] = React.useState(true);
    const [data, setData] = React.useState(undefined as unknown as IRhapsodyServer[]);
    const [selectedNamespace, setSelectedNamespace] = useCookie<string>('selectedNamespace', '');
    const selectedServer = state.selectedSever ?? 0;

    const setSelectedServer = (index: number) => {
        dispatch({ type: "setSelectedServer", payload: index });
    };

    const roleContext = React.useContext(RolesContext);
    const currentRole = roleContext.state.role;
    const headerInfo = state.headerInfo ? state.headerInfo : data as IRhapsodyServer[];

    React.useEffect(() => {
        if (!state.headerInfo) {
            setLoading(true);
            getRhapsodyHeader(state.webToken, (serverData: IRhapsodyServer[]) => {
                setLoading(false);
                setError("");
                setData(serverData);
                dispatch({ type: "setHeaderInfo", payload: serverData });
            }, (errorMessage: string) => {
                setLoading(false);
                setError(errorMessage);
            });
        }
    }, [state.webToken, currentRole]);

    const loadServers = () => {
        if (headerInfo && headerInfo[selectedServer]) {
            setLoading(true);
            getQveraEndpoints(headerInfo[selectedServer].id, selectedNamespace, state.webToken, (serverData: IQveraEndpoint[]) => {
                setLoading(false);
                setEndpoints(serverData);
            }, (erroMessage: string) => {
                setLoading(false);
                setError(erroMessage);
            });
        }
    };

    React.useEffect(loadServers, [state.webToken, selectedServer, selectedNamespace]);

    const changeSortColumn = (newSortColumn: SORT_COLUMNS) => () => {
        if (newSortColumn === sortColumn) {
            setSortAsc(!sortAsc);
        } else {
            setSortColumn(newSortColumn);
            setSortAsc(true);
        }
    };

    return (
        <ContentContainer>
            <LoadingIndicator type="Linear" show={loading} />
            {error && <ErrorBox>{error}</ErrorBox>}
            <ServerSelector headerInfo={headerInfo} selectedNamespace={selectedNamespace} setSelectedNamespace={setSelectedNamespace} selectedServer={selectedServer} setSelectedServer={setSelectedServer} />
            <TableContainer>
                <TitleBar>Endpoints</TitleBar>
                <StyledTable>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell className={(sortColumn === "zoneName" && sortAsc) ? "sort-asc" : "sort-desc"}>
                                Zone
                                <FontAwesomeIcon icon={faArrowUp} onClick={changeSortColumn("zoneName")} />
                            </TableHeaderCell>
                            <TableHeaderCell className={(sortColumn === "channelName" && sortAsc) ? "sort-asc" : "sort-desc"}>
                                Channel
                                <FontAwesomeIcon icon={faArrowUp} onClick={changeSortColumn("channelName")} />
                            </TableHeaderCell>
                            <TableHeaderCell className={(sortColumn === "endpointType" && sortAsc) ? "sort-asc" : "sort-desc"}>
                                Node Type
                                <FontAwesomeIcon icon={faArrowUp} onClick={changeSortColumn("endpointType")}/>
                            </TableHeaderCell>
                              <TableHeaderCell className={(sortColumn === "nodeName" && sortAsc) ? "sort-asc" : "sort-desc"}>
                                Node Name
                                <FontAwesomeIcon icon={faArrowUp} onClick={changeSortColumn("nodeName")}/>
                            </TableHeaderCell>
                              <TableHeaderCell className={(sortColumn === "endpointStatus" && sortAsc) ? "sort-asc" : "sort-desc"}>
                                Node Status
                                <FontAwesomeIcon icon={faArrowUp} onClick={changeSortColumn("endpointStatus")}/>
                            </TableHeaderCell>
                              <TableHeaderCell className={(sortColumn === "endpoint" && sortAsc) ? "sort-asc" : "sort-desc"}>
                                Endpoint
                                <FontAwesomeIcon icon={faArrowUp} onClick={changeSortColumn("endpoint")}/>
                            </TableHeaderCell>
                          </TableRow>
                    </TableHeader>
                    <tbody>
                        {endpoints && endpoints.sort(sortEndpoints(sortColumn, sortAsc)).map((endpoint, index) => {
                            return (
                                <TableRow key={`${endpoint.serverId}-endpoint-${index}`}>
                                    <TableCell>{endpoint.zoneName}</TableCell>
                                    <TableCell>{endpoint.channelName}</TableCell>
                                    <TableCell>{endpoint.endpointType}</TableCell>
                                    <TableCell>{endpoint.nodeName}</TableCell>
                                    <TableCell>{endpoint.endpointStatus}</TableCell>
                                    <TableCell>{endpoint.endpoint}</TableCell>                                      
                                </TableRow>
                            );
                        })}
                    </tbody>
                </StyledTable> 
            </TableContainer>
        </ContentContainer>
    );
};

export default QveraEndpoints;

